import {
  Box,
  FieldText,
  IconButton,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { ChangeEvent, useCallback, useContext, useState } from 'react';
import { useSetState } from 'react-use';

import { flowsApi } from 'apis/flows';
import { AnnouncementPicker } from 'components/shared/announcementPicker';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import {
  Dialog,
  TriggerType,
} from 'components/shared/announcementPicker/announcementPicker';
import { TimezoneSelector } from 'components/timezoneSelector';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getExceptionsFromHolidays } from 'utils/holidays';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import { OpeningHoursContext } from '../../openingHours.context';
import type { ScheduleModalProps } from '../../openingHours.view';
import { ScheduleEditPageState } from '../../views/schedule/components/scheduleItem/scheduleItem';
import { HolidayCalendarSelector } from '../holidayCalendarSelector/holidayCalendarSelector';

import styles from './addModal.module.scss';
import { getDefaultRegularOpeningHours } from './utils/openingHours';

const nameField = 'name';
const timezoneField = 'timezone';

type Props = {
  onClose: ScheduleModalProps['onClose'];
};

type State = {
  audioMessage: null | AnnouncementItem;
  countryCode: null | string;
  isClosed: boolean;
  name: string;
  timezone: string;
};

export const AddModal = ({ onClose }: Props): JSX.Element => {
  const { schedules } = useContext(OpeningHoursContext);
  const isCreatingFirstSchedule = schedules.length === 0;
  const [{ audioMessage, countryCode, isClosed, name, timezone }, setState] =
    useSetState<State>({
      audioMessage: null,
      countryCode: null,
      isClosed: false,
      name: '',
      timezone: '',
    });
  const [step, setStep] = useState(1);
  const [audioMessageDialog, setAudioMessageDialog] = useState<Dialog>(
    Dialog.Default,
  );

  const [createSchedule, { isLoading: isFlowScheduleLoading }] =
    flowsApi.useCreateFlowScheduleMutation();

  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const navigate = useAppNavigate();
  const { user } = useCurrentUser();
  const agentName = getUserDisplayName(user);

  const handleSubmit = useCallback(async () => {
    try {
      ampli.businessHoursAddScheduleFormSubmitted({
        ...(countryCode && { countryCode, isClosed }),
      });

      const holidaysResponse = countryCode
        ? await dispatch(
            flowsApi.endpoints.readFlowsSchedulesHolidays.initiate(
              {
                country_code: countryCode,
                timezone,
              },
              {
                forceRefetch: true,
              },
            ),
          )
        : null;

      const exceptions = holidaysResponse
        ? [
            ...getExceptionsFromHolidays({
              agentName,
              holidays: holidaysResponse.data?.data.holidays,
              isClosed,
            }),
          ]
        : [];

      if (audioMessage) {
        const scheduleResponse = await createSchedule({
          country_code: countryCode,
          created_at: moment.utc().toISOString(),
          name,
          timezone,
          announcement: audioMessage,
          opening_hours: [
            ...getDefaultRegularOpeningHours(agentName),
            ...exceptions,
          ],
        });

        if ('data' in scheduleResponse) {
          notify(
            {
              title: 'Schedule created!',
              type: 'success',
              time: 5000,
            },
            NotificationType.Success,
          );

          const state: ScheduleEditPageState = { isCreatingFirstSchedule };

          navigate(
            {
              path: '/settings/businesshours/:id',
              params: {
                path: {
                  id: scheduleResponse.data.id,
                },
              },
            },
            { state },
          );
        } else {
          notify(
            {
              title: 'An error occurred when trying to create schedule.',
            },
            NotificationType.Error,
          );
        }
      }
    } catch (error) {
      console.warn(error);

      notify(
        {
          title: 'An error occurred when trying to create schedule.',
        },
        NotificationType.Error,
      );
    }
  }, [
    isCreatingFirstSchedule,
    agentName,
    audioMessage,
    countryCode,
    isClosed,
    navigate,
    name,
    timezone,
    createSchedule,
    dispatch,
    notify,
  ]);

  const handleTimezoneChange = useCallback(
    (timezone?: string): void => {
      if (!timezone) return;

      setState({ timezone });
    },
    [setState],
  );

  const handleNameChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>): void => {
      if (!event) return;

      setState({ name: event.target.value });
    },
    [setState],
  );

  const handleAudioMessageSelect = useCallback(
    (audioMessage: AnnouncementItem, type: TriggerType) => {
      ampli.businessHoursAddScheduleAudioMessageAdded({ type });
      setState({ audioMessage });
    },
    [setState],
  );

  const handleCalendarChange = useCallback(
    (countryCode: null | string, isClosed: boolean) =>
      void setState({ countryCode, isClosed }),
    [setState],
  );

  const handleCancelClick = useCallback(() => {
    ampli.businessHoursAddScheduleFormCancelled();
    onClose();
  }, [onClose]);

  const isFirstStep = step === 1;

  return (
    <Modal isOpen onClose={onClose}>
      <Box width="600px">
        <form onSubmit={handleSubmit}>
          <ModalHead
            className={styles.modalHead}
            title={
              <>
                {!isFirstStep && (
                  <IconButton
                    icon="angle-left"
                    iconSize={24}
                    onClick={() => setStep(1)}
                    variant="tertiary"
                  />
                )}
                {isFirstStep ? 'Add new schedule' : 'Set audio message'}
                <span className={styles.steps}>Step {step} of 2</span>
              </>
            }
            onClose={onClose}
          />
          <ModalBody>
            {isFirstStep ? (
              <>
                <FieldText
                  label="Name"
                  placeholder="e.g. 'Italy' or 'HR Department'"
                  required
                  input={{
                    name: nameField,
                    onChange: handleNameChange,
                    value: name,
                  }}
                />
                <TimezoneSelector
                  placeholder="Select the timezone for this schedule"
                  required
                  input={{
                    name: timezoneField,
                    onChange: handleTimezoneChange,
                    value: timezone,
                  }}
                />
                <HolidayCalendarSelector
                  clearable
                  onChange={handleCalendarChange}
                />
              </>
            ) : (
              <>
                {audioMessageDialog == Dialog.Default && (
                  <Text.Paragraph variant="2" subdued withMarginBottom>
                    Set up an audio message which your customers hear when they
                    call you outside your business hours.
                  </Text.Paragraph>
                )}
                <AnnouncementPicker
                  adapter={AdapterTypes.Announcement}
                  fieldProps={{
                    label: 'Standard audio message (outside business hours)',
                  }}
                  onDialogChange={setAudioMessageDialog}
                  onSelect={handleAudioMessageSelect}
                  selectedAudio={audioMessage}
                />
              </>
            )}
          </ModalBody>
          <ModalFoot
            cancelOnClick={handleCancelClick}
            confirmProps={{
              isLoading: isFlowScheduleLoading,
              disabled: isFirstStep ? !(name && timezone) : !audioMessage,
            }}
            confirmName={isFirstStep ? 'Next' : 'Create schedule'}
            confirmOnClick={isFirstStep ? () => setStep(2) : handleSubmit}
          />
        </form>
      </Box>
    </Modal>
  );
};
